import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Flex,
  Table,
  Tag,
  Spin,
  message,
  Statistic,
  Tooltip,
  Popconfirm,
} from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { EditOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons'
import Title from 'antd/es/typography/Title'
import type { Campaign, Disposition } from '@/data'
import { dispositionLabel } from '@/data/dispositions'
import { getCampaigns } from '@/api/lop/campaigns/getCampaigns'
import { deleteCampaign } from '@/api/lop/campaigns/deleteCampaign'
import { executeCampaigns } from '@/api/lop/campaigns/executeCampaigns'
import { getDispositions } from '@/api/lop/dispositions/getDispositions'

import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'

export const SmsRemarketingList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [dispositions, setDispositions] = useState<Disposition[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortField, setSortField] = useState('-created_at')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  })
  const hasFetched = useRef(false)
  const canRead = hasPermission(auth.user, 'lop:ui:action:read:campaigns')
  const canCreate = hasPermission(auth.user, 'lop:ui:action:create:campaigns')
  const canUpdate = hasPermission(auth.user, 'lop:ui:action:update:campaigns')
  const canDelete = hasPermission(auth.user, 'lop:ui:action:delete:campaigns')

  const columnSortKeys = {
    name: 'name',
    created_at: 'created_at',
  } as const

  const fetchCampaigns = useCallback(
    async (page = 1, size = pagination.pageSize, sort = sortField) => {
      try {
        setLoading(true)
        setError(null)
        const { data, meta } = await getCampaigns({
          page,
          per_page: size,
          sort,
          include: 'count',
        })
        setCampaigns(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load campaigns')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [pagination.pageSize, sortField],
  )

  useEffect(() => {
    if (!canRead) return
    if (!hasFetched.current) {
      hasFetched.current = true
    }
    fetchCampaigns()
  }, [canRead, fetchCampaigns])

  useEffect(() => {
    getDispositions({ per_page: 100, sort: 'disposition' })
      .then(({ data }) => setDispositions(data))
      .catch((err) => console.error('Failed to load dispositions', err))
  }, [])

  const dispositionLabelMap = useMemo(
    () => new Map(dispositions.map((d) => [d.id, dispositionLabel(d)])),
    [dispositions],
  )

  const totalPending = campaigns.reduce(
    (sum, c) => sum + Number(c.audience_count || 0),
    0,
  )

  const handleSendAll = async () => {
    if (totalPending <= 0) return
    try {
      setSending(true)
      await executeCampaigns(campaigns.map((c) => c.id))
      message.success(
        `Started sending ${totalPending.toLocaleString()} messages`,
      )
    } catch {
      message.error('Failed to start send')
    } finally {
      setSending(false)
    }
  }

  const handleSendCampaign = async (campaign: Campaign) => {
    try {
      setSending(true)
      await executeCampaigns([campaign.id])
      const count = Number(campaign.audience_count || 0)
      message.success(
        count > 0
          ? `Started sending ${count.toLocaleString()} messages for ${campaign.name}`
          : `Started sending campaign: ${campaign.name}`,
      )
    } catch {
      message.error(`Failed to start send for ${campaign.name}`)
    } finally {
      setSending(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteCampaign(String(id))
      setCampaigns((prev) => prev.filter((c) => c.id !== id))
      message.success('Campaign deleted')
    } catch {
      message.error('Failed to delete campaign')
    }
  }

  const columns: TableProps<Campaign>['columns'] = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) =>
        canUpdate ? (
          <Link to={`/sms-remarketing/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Dispositions',
      key: 'dispositions',
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {(record.crm_disposition_mapping_ids ?? []).map((d) => (
            <Tag key={d}>{dispositionLabelMap.get(d) ?? String(d)}</Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: 'Templates',
      key: 'templateCount',
      render: (_, record) => (record.campaign_templates ?? []).length,
    },
    {
      title: 'Audience',
      dataIndex: 'audience_count',
      key: 'audience_count',
      render: (count: string) => {
        const n = Number(count || 0)
        return (
          <span style={{ fontWeight: n > 0 ? 600 : 400 }}>
            {n.toLocaleString()}
          </span>
        )
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdate ? (
            <Link to={`/sms-remarketing/${record.id}/edit`}>
              <EditOutlined style={{ fontSize: 16 }} />
            </Link>
          ) : null}
          {canUpdate ? (
            <Popconfirm
              title="Send this campaign now?"
              description={
                Number(record.audience_count || 0) > 0
                  ? `This will send ${Number(
                      record.audience_count,
                    ).toLocaleString()} messages for "${record.name}".`
                  : `This will send messages for "${record.name}".`
              }
              okText="Send"
              cancelText="Cancel"
              okButtonProps={{ loading: sending }}
              onConfirm={() => handleSendCampaign(record)}
              disabled={sending}
              placement="topRight"
            >
              <Tooltip title="Send campaign">
                <SendOutlined
                  style={{
                    fontSize: 16,
                    color: sending ? '#bfbfbf' : '#1677ff',
                    cursor: sending ? 'not-allowed' : 'pointer',
                  }}
                />
              </Tooltip>
            </Popconfirm>
          ) : null}
          {canDelete ? (
            <DeleteOutlined
              style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
              onClick={() => handleDelete(record.id)}
            />
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>SMS Remarketing Campaigns</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchCampaigns()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Title level={4}>SMS Remarketing Campaigns</Title>
        <Flex gap="small">
          {canCreate ? (
            <Button
              type="primary"
              onClick={() => navigate('/sms-remarketing/new')}
            >
              New Campaign
            </Button>
          ) : null}
        </Flex>
      </div>

      <Flex
        align="center"
        justify="space-between"
        style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: '#fafafa',
          borderRadius: 8,
        }}
      >
        <Statistic
          title="Total Messages Available Today"
          value={totalPending}
          style={{ marginRight: 32 }}
        />
        <Popconfirm
          title="Send all messages now?"
          description={`This will send ${totalPending.toLocaleString()} messages across all campaigns.`}
          okText="Send"
          cancelText="Cancel"
          okButtonProps={{ loading: sending }}
          onConfirm={handleSendAll}
          disabled={totalPending === 0 || sending}
        >
          <Button
            type="primary"
            icon={<SendOutlined />}
            size="large"
            loading={sending}
            disabled={totalPending === 0}
          >
            Send All
          </Button>
        </Popconfirm>
      </Flex>

      {canRead ? (
        <Spin spinning={loading}>
          <Table<Campaign>
            rowKey="id"
            columns={columns}
            dataSource={campaigns}
            pagination={pagination}
            onChange={(p, _filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-created_at',
              )
              setSortField(sort)
              fetchCampaigns(p.current, p.pageSize, sort)
            }}
          />
        </Spin>
      ) : null}
    </div>
  )
}
